import React from 'react';
import { AbstractButton, Box, Card, Flex, Icon, IconButton, Modal, Tooltip } from 'pounceV2';
import DOMPurify from 'dompurify';
import { useTheme } from '@mui/material';

import useMermaidV3 from 'Hooks/useMermaidV3';
import useCopyToClipboardV3 from 'Hooks/useCopyToClipboardV3';
import GenericCodeBlockV3 from 'Components/CodeBlock/GenericCodeBlockV3';
import { PRINT_SUPPRESS_ATTR } from 'Components/utils/printAIResponse';

// Splits large words in timeline diagrams.
// This is to prevent the diagram from overflowing the cards
// which is a known issue with mermaid library
export const processTimelineDiagram = (mermaidText: string): string => {
  // Only process if it's a timeline diagram
  if (!mermaidText.trim().startsWith('timeline')) {
    return mermaidText;
  }

  const lines = mermaidText.split('\n');
  return lines
    .map(line => {
      // Only process lines that have a colon or start with a section
      if (line.match(/^.+:.+$/) || line.match(/^\s*section .+$/)) {
        // Process each word in the content
        const processedContent = line
          .split(' ')
          .map(word => {
            // If word is longer than 14 characters, split it with spaces every 14 chars
            if (word.length > 14) {
              const chunks = [];
              let i = 0;
              while (i < word.length) {
                chunks.push(word.substring(i, i + 14));
                i += 14;
              }
              return chunks.join('<br>');
            }
            return word;
          })
          .join(' ');

        return processedContent;
      }
      return line;
    })
    .join('\n');
};

const ModalButtons = ({
  textToCopy,
  closeModal,
}: {
  textToCopy: string;
  closeModal: () => void;
}) => {
  const copyToClipboard = useCopyToClipboardV3();
  return (
    <Flex position="absolute" right={16} top={16} gap={1}>
      <Tooltip content="Copy Mermaid diagram code" placement="top">
        <IconButton
          variant="outline"
          size="medium"
          icon="copy"
          aria-label="Copy Mermaid diagram code"
          onClick={() =>
            copyToClipboard(textToCopy, {
              title: 'Mermaid diagram code copied!',
              duration: 1500,
              variant: 'success',
            })
          }
        />
      </Tooltip>
      <Tooltip content="Collapse" placement="top">
        <IconButton
          variant="outline"
          size="medium"
          icon="collapse"
          aria-label="Collapse"
          onClick={closeModal}
        />
      </Tooltip>
    </Flex>
  );
};

const FloatingButtons = ({
  textToCopy,
  openModal,
}: {
  textToCopy: string;
  openModal: () => void;
}) => {
  const {
    palette: { tokens },
  } = useTheme();
  const copyToClipboard = useCopyToClipboardV3();
  return (
    <Flex
      backgroundColor={tokens.background.neutral.canvas.base}
      borderRadius="8px"
      position="absolute"
      right={-8}
      top={-8}
      p="1px"
      gap={1}
      {...{ [PRINT_SUPPRESS_ATTR]: true }}
    >
      <Tooltip content="Copy Mermaid diagram code" placement="top">
        <IconButton
          variant="outline"
          size="small"
          icon="copy"
          aria-label="Copy Mermaid diagram code"
          onClick={e => {
            copyToClipboard(textToCopy, {
              title: 'Mermaid diagram code copied!',
              duration: 1500,
              variant: 'success',
            });
            e.currentTarget.blur();
          }}
        />
      </Tooltip>
      <Tooltip content="Expand" placement="top">
        <IconButton
          variant="outline"
          size="small"
          icon="expand"
          aria-label="Expand Mermaid diagram"
          onClick={e => {
            openModal();
            e.currentTarget.blur();
          }}
        />
      </Tooltip>
    </Flex>
  );
};

const ZoomControls = ({
  zoomLevel,
  onZoomIn,
  onZoomOut,
  onReset,
  isFullScreen,
}: {
  zoomLevel: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
  isFullScreen: boolean;
}) => {
  const {
    palette: { componentTokens },
  } = useTheme();
  return (
    <Flex
      position="absolute"
      right={isFullScreen ? 16 : 12}
      top={isFullScreen ? 'calc(50% - 43px)' : undefined}
      bottom={isFullScreen ? undefined : 6}
      backgroundColor={componentTokens.buttons.colors.coreIcon.secondary.background.resting}
      direction="column"
      borderRadius="8px"
      p="1px"
      gap={1}
      {...{ [PRINT_SUPPRESS_ATTR]: true }}
    >
      <AbstractButton
        disabled={zoomLevel >= 5}
        size="small"
        aria-label="Zoom in"
        onClick={onZoomIn}
        alignContent="center"
        p={1}
        borderRadius="8px"
        _hover={{
          backgroundColor: componentTokens.buttons.colors.coreIcon.secondary.background.hover,
        }}
      >
        <Icon type="add" size="medium" />
      </AbstractButton>
      <AbstractButton
        disabled={zoomLevel <= 0.2}
        size="small"
        aria-label="Zoom out"
        onClick={onZoomOut}
        alignContent="center"
        p={1}
        borderRadius="8px"
        _hover={{
          backgroundColor: componentTokens.buttons.colors.coreIcon.secondary.background.hover,
        }}
      >
        <Icon type="subtract" size="medium" />
      </AbstractButton>
      <AbstractButton
        size="small"
        aria-label="Reset zoom and position"
        onClick={onReset}
        alignContent="center"
        p={1}
        borderRadius="8px"
        _hover={{
          backgroundColor: componentTokens.buttons.colors.coreIcon.secondary.background.hover,
        }}
      >
        <Icon type="fit-view" size="medium" />
      </AbstractButton>
    </Flex>
  );
};

const DangerousSvgDiv = ({
  mermaidBlock,
  isFullScreen,
}: {
  mermaidBlock: string;
  isFullScreen: boolean;
}) => {
  const mermaidHandle = useMermaidV3();
  const cachedSvg = mermaidHandle.cachedSvgs[mermaidBlock]?.svg ?? null;
  const [svg, setSvg] = React.useState(cachedSvg);

  React.useEffect(() => {
    setSvg(cachedSvg);
  }, [cachedSvg]);

  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [scale, setScale] = React.useState(1);

  React.useEffect(() => {
    const renderSingle = async () => {
      const parsed = await mermaidHandle.parse(mermaidBlock);
      if (!parsed.ok) {
        // eslint-disable-next-line no-console
        console.warn('failed to render mermaid diagram to svg', parsed.originalText);
        return;
      }
      const rendered = await mermaidHandle.renderSvg(mermaidBlock);
      // It's possible that there was an error rendering the svg. If so, we'll return
      // early and render the diagram as a code block
      if (!rendered.svg) {
        // eslint-disable-next-line no-console
        console.warn('failed to render mermaid diagram to svg', parsed.originalText);
        return;
      }
      setSvg(rendered.svg);
    };

    if (!svg) {
      renderSingle();
    }
  }, [mermaidHandle, mermaidBlock, setSvg, svg]);

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
  }, [setIsDragging]);

  React.useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseUp]);

  const handleMouseDown = React.useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      e.preventDefault(); // Prevent text selection during drag
    },
    [setIsDragging, setDragStart],
  );

  const handleMouseMove = React.useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) {
        return;
      }

      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;

      setPosition({
        x: position.x + dx,
        y: position.y + dy,
      });
      setDragStart({ x: e.clientX, y: e.clientY });
    },
    [isDragging, dragStart, position, setPosition],
  );

  const handleDoubleClick = React.useCallback(() => {
    setPosition({ x: 0, y: 0 });
    setScale(1);
  }, [setPosition, setScale]);

  const handleZoomIn = React.useCallback(() => {
    setScale(s => Math.min(s * 1.2, 5)); // Max zoom 5x
  }, []);

  const handleZoomOut = React.useCallback(() => {
    setScale(s => Math.max(s / 1.2, 0.2)); // Min zoom 0.2x
  }, []);

  const handleReset = React.useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const sanitizedSvg = React.useMemo(() => {
    if (svg === null) {
      return null;
    }
    // this can fire multiple times so to be safe we remove the hook before re-adding it
    DOMPurify.removeHook('uponSanitizeElement');
    DOMPurify.addHook('uponSanitizeElement', (node, event) => {
      if (event?.tagName === 'a') {
        const textNode = document.createTextNode(node.textContent);
        node.parentNode.replaceChild(textNode, node);
      }
    });
    return DOMPurify.sanitize(svg, {
      ADD_TAGS: ['foreignObject', 'use'],
      HTML_INTEGRATION_POINTS: { foreignobject: true },
    });
  }, [svg]);

  // still generating the svg string and/or failed to render mermaid diagram.
  // fall back to displaying it as a code block
  if (sanitizedSvg === null) {
    return (
      <GenericCodeBlockV3 language="mermaid" data-testid="mermaid-diagram-card">
        {mermaidBlock}
      </GenericCodeBlockV3>
    );
  }

  return (
    <Card
      data-testid="mermaid-diagram-card"
      borderRadius="large"
      display="flex"
      height="calc(100% - 16px)"
      m={2}
    >
      {/* We are using custom pan and zoom controls here
          Another option would be to wrap the svg in ReactFlow which provides
          pan and zoom out of the box but we need to find a proper way to calculate
          the size of the diagram so that we can set the correct initial scale and position */}
      <Flex
        onMouseMove={handleMouseMove}
        onDoubleClick={handleDoubleClick}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        cursor={isDragging ? 'grabbing' : 'grab'}
        overflow="hidden"
        p="24px"
        // This is to account for the zoom controls
        pr="50px"
        flex={1}
      >
        <div
          data-testid="mermaid-diagram-svg"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: 'center',
          }}
          dangerouslySetInnerHTML={{ __html: sanitizedSvg }}
        />
      </Flex>
      <ZoomControls
        isFullScreen={isFullScreen}
        zoomLevel={scale}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onReset={handleReset}
      />
    </Card>
  );
};

export const processFlowchartDiagram = (mermaidText: string): string => {
  // Only process if it's a flowchart/graph diagram
  const trimmed = mermaidText.trim();
  if (!trimmed.startsWith('flowchart') && !trimmed.startsWith('graph')) {
    return mermaidText;
  }

  const lines = mermaidText.split('\n');
  return lines
    .filter(line => {
      const trimmedLine = line.trim();
      // these lines are present if the AI tried to emit custom styling.
      // we prevent this by removing the custom style code in favor of our panther styles
      if (trimmedLine.startsWith('style') || trimmedLine.startsWith('classDef')) {
        return false;
      }
      return true;
    })
    .join('\n');
};

export const preProcessMermaidBlock = (block: string): string => {
  let outString = block;
  outString = processTimelineDiagram(outString);
  outString = processFlowchartDiagram(outString);
  return outString;
};

const MermaidDiagramV3 = ({ mermaidBlock }: { mermaidBlock: string }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const processedMermaidBlock = React.useMemo(
    () => preProcessMermaidBlock(mermaidBlock),
    [mermaidBlock],
  );

  return (
    <Box position="relative" my={2} mr={3}>
      <DangerousSvgDiv mermaidBlock={processedMermaidBlock} isFullScreen={false} />
      {/* We are using an inline modal here to have access to the mermaidContext */}
      {/* overlayZIndex must be higher than MUI Drawer z-index (1200) to render above parent containers */}
      <Modal
        fullScreen
        padded={false}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        overlayZIndex={1400}
      >
        {/* Use simple grid to ensure the diagram is full screen  and centered */}
        {isModalOpen && (
          <>
            <Box width="100%" height="100%" overflow="hidden">
              <DangerousSvgDiv mermaidBlock={processedMermaidBlock} isFullScreen={true} />
            </Box>
            <ModalButtons
              textToCopy={mermaidBlock}
              closeModal={() => {
                setIsModalOpen(false);
              }}
            />
          </>
        )}
      </Modal>
      <FloatingButtons textToCopy={mermaidBlock} openModal={() => setIsModalOpen(true)} />
    </Box>
  );
};

export default React.memo(MermaidDiagramV3);
